<script setup lang="ts">
  /**
   * GamesList — "Popular Games" rail module.
   *
   * Measured against yahoo.com's games card (US): a 300px card at 12px
   * radius with 16px/20px padding, an 18px/23px bold heading, and three
   * rows in a 252px-tall column spaced 16px apart. Each row is 110x73
   * artwork at 8px radius beside an optional badge, a 15px semibold title
   * that wraps, and a 12px meta line. Hovering the artwork reveals their
   * "Play now" pill.
   */
  import type { GamesListItem, GamesListProps } from './types'

  withDefaults(defineProps<GamesListProps>(), {
    heading: 'Popular Games',
    items: () => [],
  })

  // Reports the click and lets the anchor navigate on its own. Whoever
  // renders this decides what a click means (common's PortalGamesList
  // tracks it), so no analytics reach this layer.
  const emit = defineEmits<{ select: [item: GamesListItem] }>()
</script>

<template>
  <section class="w-full rounded-lg bg-surface-primary px-4 py-5">
    <!-- Yahoo's heading links to their games hub. Ours is plain text, so it
         gets no chevron either, since nothing here navigates. -->
    <h2 class="mb-3 text-[18px] font-bold leading-[23px] text-content-primary">
      {{ heading }}
    </h2>

    <!-- min-height + justify-between keeps the card the same height whether
         a row carries a badge or a two-line title, like Yahoo's -->
    <ul class="flex min-h-[15.75rem] flex-col justify-between gap-4">
      <li v-for="item in items" :key="item.id">
        <component
          :is="item.href ? 'a' : 'div'"
          :href="item.href"
          :target="item.href ? '_blank' : undefined"
          :rel="item.href ? 'noopener noreferrer' : undefined"
          class="flex w-full min-w-0 items-center gap-2"
          :class="item.href ? 'cursor-pointer' : ''"
          @click="emit('select', item)"
        >
          <div class="group relative h-[73px] w-[110px] shrink-0">
            <img
              :src="item.imageUrl"
              :alt="item.title"
              class="h-full w-full rounded-md object-cover"
              loading="lazy"
              width="110"
              height="73"
            />
            <span
              v-if="item.href"
              class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-[var(--yp-chip-border)] bg-surface-primary px-2.5 py-1.5 text-sm font-semibold leading-4 text-content-primary opacity-0 transition-opacity group-hover:opacity-100"
            >
              Play now
            </span>
          </div>

          <div class="flex min-w-0 flex-col">
            <span
              v-if="item.badge"
              class="mb-0.5 self-start rounded-full bg-[#f8fb4c] px-1.5 py-px text-[10px] font-medium leading-[14px] tracking-[0.2px] text-black"
            >
              {{ item.badge }}
            </span>
            <p
              class="text-[15px] font-semibold leading-[1.6rem] text-content-primary"
            >
              {{ item.title }}
            </p>
            <p
              v-if="item.meta"
              class="text-[12px] leading-4 tracking-[-0.12px] text-content-secondary"
            >
              {{ item.meta }}
            </p>
          </div>
        </component>
      </li>
    </ul>
  </section>
</template>
